<template>
  <PreviewSheet class="gallery-preview" radius="0">
    <div class="flex-col justify-center gap-10">
      <div class="gallery-preview__text flex-col gap-4">
        <PreviewTypography :text="headerContent" variant="heading2" />
        <PreviewTypography
          v-if="subtitle"
          :text="subtitle"
          variant="paragraphRegular"
        />
        <PreviewButton v-if="actionLabel" :title="actionLabel" caret />
      </div>

      <OwnGrid class="gallery-preview__img-grid">
        <PreviewSheet
          v-for="(item, idx) in previewItems"
          :key="idx"
          class="gallery-preview__img"
          :style="{ backgroundImage: `url(${item.thumbnail})` }"
        >
          <span
            v-if="item.type === 'video'"
            aria-hidden="true"
            class="gallery-preview__play"
          />
        </PreviewSheet>
      </OwnGrid>
    </div>
  </PreviewSheet>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import PreviewButton from '@/lib/builder/public/preview/PreviewButton.vue'
import PreviewSheet from '@/lib/builder/public/preview/PreviewSheet.vue'
import PreviewTypography from '@/lib/builder/public/preview/PreviewTypography.vue'
import { OwnGrid } from '@/ui/OwnGrid'

import type { GallerySection } from '../../../WebsiteBuilder.types'

const props = defineProps<{
  content: GallerySection
}>()

const currentBlockParams = computed(() => {
  return props.content?.params
})

type GalleryMediaItem = NonNullable<GallerySection['params']['media']>[number]

const mediaItems = computed<GalleryMediaItem[]>(() => {
  return currentBlockParams.value?.media ?? []
})

const previewItems = computed(() =>
  mediaItems.value.map((item) => ({
    thumbnail: item.type === 'image' ? item.params.src : item.params.thumbnail,
    type: item.type,
  }))
)

const headerContent = computed(() => currentBlockParams.value.title)

const subtitle = computed(() => currentBlockParams.value.description)
const actionLabel = computed(
  () => currentBlockParams.value?.action?.params?.label ?? null
)
</script>

<style lang="scss" scoped>
.gallery-preview {
  padding: 120px;

  &__text {
    max-width: 672px;
    padding: 0 16px;
    text-align: left;
  }

  &__img-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    flex: 1;
    width: 100%;
  }

  &__img {
    aspect-ratio: 1 / 1;
    background-size: cover;
    position: relative;
  }

  &__play {
    align-items: center;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 999px;
    display: flex;
    height: 48px;
    justify-content: center;
    left: 50%;
    padding-left: 3px;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 48px;
  }

  &__play::before {
    border-bottom: 8px solid transparent;
    border-left: 12px solid #111;
    border-top: 8px solid transparent;
    content: '';
  }
}
</style>
